import type { PostHog } from "posthog-js";

// Module-level state: one client, one init. posthog-js is imported dynamically so
// it stays out of the bundle's critical path and is never loaded when disabled.
let client: PostHog | null = null;
let initPromise: Promise<void> | null = null;

function getKey(): string | undefined {
  // Read lazily so env stubbing order relative to module import never
  // matters in tests, and isAnalyticsEnabled() always reflects current env.
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  return key || undefined;
}

export function isAnalyticsEnabled(): boolean {
  return Boolean(getKey());
}

export function initAnalytics(): void {
  const key = getKey();
  if (!key || initPromise) return;
  initPromise = import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(key, {
        api_host:
          (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
          "https://eu.i.posthog.com",
        // The "2025-05-24" preset sets capture_pageview: "history_change":
        // SPA navigations capture immediately, and the library captures the
        // initial-load pageview once the tab is VISIBLE (hidden/prerendered
        // tabs defer until viewed — so it never appears in headless/hidden
        // test tabs). Never capture it manually: that double-counts, verified
        // live (one visible-tab load → two $pageview events).
        defaults: "2025-05-24",
        autocapture: false,
        disable_session_recording: true,
        person_profiles: "identified_only",
        persistence: "localStorage",
      });
      client = posthog;
    })
    .catch((err) => {
      console.error("Analytics init failed:", err);
      // A transient chunk-load failure must not disable analytics for the
      // whole session — clearing the promise lets a later init retry.
      initPromise = null;
    });
}

// The trailing .catch on these chains is the fire-and-forget guarantee:
// analytics failures must never surface as app errors (unhandled rejections).

export function capture(event: string, properties?: Record<string, unknown>): void {
  initPromise?.then(() => client?.capture(event, properties)).catch(() => {});
}

export function identify(userId: string): void {
  initPromise?.then(() => client?.identify(userId)).catch(() => {});
}

// Called on sign-out: drops the identified distinct id so later activity on
// this browser (e.g. a guest on a shared device) starts a fresh anonymous
// identity instead of being attributed to the signed-out user.
export function reset(): void {
  initPromise?.then(() => client?.reset()).catch(() => {});
}
